import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// 与 CourseCard 所需字段保持一致的课程形状
export interface GroupedCourse {
    id: string;
    name: string;
    nameEn?: string;
    avatar?: string | null;
    scheduleInfo: string;
    status: string;
    location?: string;
    description?: string;
    hasTodayClass?: boolean;
}

export interface CourseGroup {
    programId: string;
    title: string;
    subtitle?: string;
    status?: string;
    courses: GroupedCourse[];
}

/**
 * 根据传入的课程列表，从 programs 集合里找到每个课程所属的项目，并按项目分组返回。
 * 一门课程若同时属于多个项目，会在每个项目分组下各出现一次，便于区分不同届次。
 * 不属于任何项目的课程会被放到「未分组」分组下，避免遗漏。
 * 传入 programStatus 时，只返回状态匹配的项目分组（如 'active'），
 * 用于“只看某个状态项目下的课程”；此时未归属任何匹配项目的课程不会展示。
 */
export const useProgramGroupedCourses = (courses: GroupedCourse[], programStatus?: string): CourseGroup[] => {
    const [groups, setGroups] = useState<CourseGroup[]>([]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!courses || courses.length === 0) {
                setGroups([]);
                return;
            }

            try {
                const snap = await getDocs(collection(db, 'programs'));
                if (cancelled) return;

                const courseIdSet = new Set(courses.map(c => c.id));
                const courseById = new Map(courses.map(c => [c.id, c]));
                const programMap = new Map<string, CourseGroup>();
                const groupedIds = new Set<string>();

                for (const doc of snap.docs) {
                    const data = doc.data();
                    const programCourses: GroupedCourse[] = [];
                    const courseGroups = data.courseGroups || [];

                    for (const group of courseGroups) {
                        const groupCourses = group.courses || [];
                        for (const cRef of groupCourses) {
                            const cid = typeof cRef === 'string'
                                ? cRef
                                : (cRef?.id || (cRef?.path ? cRef.path.split('/').pop() : ''));
                            if (cid && courseIdSet.has(cid)) {
                                const matched = courseById.get(cid);
                                if (matched) {
                                    programCourses.push(matched);
                                    groupedIds.add(cid);
                                }
                            }
                        }
                    }

                    if (programCourses.length > 0) {
                        programMap.set(doc.id, {
                            programId: doc.id,
                            title: data.title || '',
                            subtitle: data.subtitle || '',
                            status: data.status || '',
                            courses: programCourses
                        });
                    }
                }

                if (cancelled) return;

                let groupsArr = Array.from(programMap.values());
                if (programStatus) {
                    groupsArr = groupsArr.filter(g => g.status === programStatus);
                }

                const orphanCourses = courses.filter(c => !groupedIds.has(c.id));
                if (orphanCourses.length > 0 && !programStatus) {
                    groupsArr.push({
                        programId: '',
                        title: '未分组',
                        status: '',
                        courses: orphanCourses
                    });
                }
                setGroups(groupsArr);
            } catch (err) {
                console.error('获取项目分组失败:', err);
            }
        };

        run();
        return () => { cancelled = true; };
    }, [courses, programStatus]);

    return groups;
};
